import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router";
import { getAuthErrorMessage, isAuthorizationError } from "@/features/auth/model/auth-errors";
import { useDeployments } from "@/features/deployment-selection/hooks/use-deployments";
import { DeploymentCard } from "@/features/deployment-selection/ui/deployment-card";
import { ApiError } from "@/shared/api/api-error";
import { Callout } from "@/shared/ui/callout";
import { LoadingState } from "@/shared/ui/loading-state";

interface DeploymentSelectionErrorProps {
  error: unknown;
}

function DeploymentSelectionError({ error }: DeploymentSelectionErrorProps) {
  if (error instanceof ApiError) {
    if (error.code === "NETWORK_ERROR") {
      return (
        <Callout severity="error" title="Cannot connect to the customer API">
          The resolved customer API could not be reached. Check the API is available and allows
          browser requests from this origin.
        </Callout>
      );
    }

    if (error.statusCode === 401) {
      return <Callout severity="error">Authentication required. Please sign in again.</Callout>;
    }

    if (error.statusCode === 403) {
      return (
        <Callout severity="error" title="Access denied">
          {isAuthorizationError(error.code)
            ? getAuthErrorMessage(error.code)
            : "You do not have permission to view deployments."}
        </Callout>
      );
    }

    return (
      <Callout severity="error" title="Deployments could not be loaded">
        {error.message} ({error.statusCode})
      </Callout>
    );
  }

  return (
    <Callout severity="error" title="Deployments could not be loaded">
      Please check your connection and try again.
    </Callout>
  );
}

function getReportDownloadPath(deploymentId: string, continuePath: string | null): string | null {
  const match = /^\/generated-reports\/download\/([^/?#]+)$/.exec(continuePath ?? "");
  if (!match?.[1]) {
    return null;
  }

  try {
    return `/deployments/${encodeURIComponent(deploymentId)}/generated-reports/download/${encodeURIComponent(
      decodeURIComponent(match[1])
    )}`;
  } catch {
    return null;
  }
}

function DeploymentSelectionEmpty() {
  return (
    <Callout severity="info" title="No deployments available">
      Contact your administrator if you expected access to a Situate deployment.
    </Callout>
  );
}

export function DeploymentSelectionView() {
  const { data: deployments, isLoading, error } = useDeployments();
  const navigate = useNavigate();
  const continuePath = new URLSearchParams(window.location.search).get("continue");

  function handleSelect(deploymentId: string) {
    const reportDownloadPath = getReportDownloadPath(deploymentId, continuePath);
    navigate(reportDownloadPath ?? `/deployments/${encodeURIComponent(deploymentId)}/dashboard`);
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: 2,
        py: { xs: 5, md: 8 },
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={4}>
          <Stack spacing={3}>
            <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
              <Box
                component="img"
                src="/icon.png"
                alt=""
                aria-hidden="true"
                sx={{ width: 30, height: 30 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                situate
              </Typography>
            </Stack>

            <Stack spacing={2}>
              <Chip
                label="Assigned deployments"
                color="primary"
                variant="outlined"
                size="small"
                sx={{
                  alignSelf: "flex-start",
                  fontSize: 10,
                  fontWeight: 900,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                }}
              />
              <Stack spacing={1}>
                <Typography variant="h2">Choose a deployment</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 680 }}>
                  Select the operational deployment you want to monitor. Deployment access and roles
                  are loaded from your resolved customer API session.
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          <Box
            sx={(theme) => ({
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
              bgcolor: alpha(theme.palette.common.black, 0.12),
            })}
          >
            {isLoading && <LoadingState message="Loading deployments..." />}
            {error && <DeploymentSelectionError error={error} />}
            {!isLoading && !error && (!deployments || deployments.length === 0) && (
              <DeploymentSelectionEmpty />
            )}
            {!isLoading && !error && deployments && deployments.length > 0 && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                    xl: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                {deployments.map((deployment) => (
                  <DeploymentCard
                    key={deployment.deployment_id}
                    deployment={deployment}
                    onSelect={handleSelect}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
